import React from "react";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import IconButton from "@material-ui/core/IconButton";
import Chip from "@material-ui/core/Chip";
import RepeatIcon from "@material-ui/icons/Repeat";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import PlayCircleOutlineIcon from "@material-ui/icons/PlayCircleOutline";
import StopIcon from "@material-ui/icons/Stop";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CampaignStatusChip from "./CampaignStatusChip";
import { isCampaignEditable, canOpenCampaignModal } from "../utils/campaignRules";
import { i18n } from "../../../translate/i18n";

const CampaignTableRow = ({
  campaign,
  classes,
  formatDateTime,
  formatScheduledDate,
  handleEditCampaign,
  handleDeleteCampaign,
  handlePauseCampaign,
  handleStopCampaign,
  restartCampaign,
  handleCopyCampaign,
  setExecutionsModalOpen,
  setSelectedCampaignForExecutions,
}) => (
  <TableRow className={classes.tableRow}>
    <TableCell align="center">{campaign.name}</TableCell>
    <TableCell align="center">
      <CampaignStatusChip status={campaign.status} classes={classes} />
    </TableCell>
    <TableCell align="center">
      {/* ✅ Usar campo 'tipo' del SP si está disponible, sino usar isRecurring */}
      {campaign.tipo === 'RECURRENTE' || campaign.isRecurring ? (
        <Chip
          icon={<RepeatIcon />}
          label="Recurrente"
          color="primary"
          size="small"
          style={{ fontWeight: 'bold' }}
        />
      ) : (
        <Chip
          label="Única"
          color="default"
          size="small"
        />
      )}
    </TableCell>
    <TableCell align="center">
      {/* ✅ MEJORA UX: Mostrar contador X/Y para todas las campañas (únicas y recurrentes) */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
        {/* Contador X/Y - Siempre visible si hay datos, o mostrar 0/0 si no hay */}
        {(() => {
          // ✅ Para campañas RECURRENTES: Usar información de currentExecution si está disponible
          let deliveredCount = campaign.deliveredCount;
          let totalContacts = campaign.totalContacts;

          if (campaign.isRecurring && campaign.currentExecution) {
            // Usar progreso de la ejecución actual
            deliveredCount = campaign.currentExecution.enviados || 0;
            totalContacts = campaign.totalContacts || 0;
          }

          return (totalContacts !== undefined && deliveredCount !== undefined) ? (
            <Chip
              label={`${deliveredCount}/${totalContacts}`}
              size="small"
              onClick={() => {
                setSelectedCampaignForExecutions(campaign);
                setExecutionsModalOpen(true);
              }}
              style={{
                backgroundColor: deliveredCount === totalContacts && totalContacts > 0
                  ? '#4caf50'
                  : deliveredCount > 0
                    ? '#ff9800'
                    : '#e3f2fd',
                color: deliveredCount === totalContacts && totalContacts > 0
                  ? 'white'
                  : deliveredCount > 0
                    ? 'white'
                    : '#1976d2',
                fontWeight: 'bold',
                minWidth: '50px',
                cursor: 'pointer'
              }}
            />
          ) : (
            <Chip
              label="0/0"
              size="small"
              onClick={() => {
                setSelectedCampaignForExecutions(campaign);
                setExecutionsModalOpen(true);
              }}
              style={{
                backgroundColor: '#e3f2fd',
                color: '#1976d2',
                fontWeight: 'bold',
                minWidth: '50px',
                cursor: 'pointer'
              }}
            />
          );
        })()}
        {/* ✅ SOLO mostrar para campañas RECURRENTES con executionCount > 0 */}
        {/* ⚠️ IMPORTANTE: NO mostrar executionCount para campañas ÚNICAS (isRecurring = 0 o false) */}
        {((campaign.tipo === 'RECURRENTE') || (campaign.isRecurring === true || campaign.isRecurring === 1)) && campaign.executionCount !== undefined && campaign.executionCount !== null && campaign.executionCount > 0 && (
          <span style={{ fontSize: '0.75rem', color: '#9e9e9e' }}>
            {campaign.executionCount} {campaign.executionCount === 1 ? 'ejecución' : 'ejecuciones'}
          </span>
        )}
      </div>
    </TableCell>
    <TableCell align="center">
      {campaign.isRecurring && campaign.nextScheduledAt ? (
        <span style={{ color: '#1976d2', fontWeight: 'bold' }}>
            {/* `nextScheduledAt` y `scheduledAt` SI son hora de PARED: se muestran tal cual.
                `completedAt` NO — es un INSTANTE, y va mas abajo con `formatDateTime`. */}
          {(formatScheduledDate || formatDateTime)(campaign.nextScheduledAt)}
        </span>
      ) : (
        <span style={{ color: '#9e9e9e' }}>-</span>
      )}
    </TableCell>
    <TableCell align="center">
      {campaign.contactListId && campaign.contactList
        ? (campaign.contactList.name && campaign.contactList.name.length > 30
            ? `${campaign.contactList.name.substring(0, 30)}...`
            : campaign.contactList.name || i18n.t("campaigns.placeholders.notDefined"))
        : i18n.t("campaigns.placeholders.notDefined")}
    </TableCell>
    <TableCell align="center">
      {campaign.whatsappId && campaign.whatsapp
        ? (campaign.whatsapp.name || i18n.t("campaigns.placeholders.notDefinedMale"))
        : i18n.t("campaigns.placeholders.notDefinedMale")}
    </TableCell>
    <TableCell align="center">
      {campaign.prompt && campaign.prompt.name
        ? campaign.prompt.name
        : "—"}
    </TableCell>
    <TableCell align="center">
      {campaign.scheduledAt
        ? (formatScheduledDate || formatDateTime)(campaign.scheduledAt)
        : i18n.t("campaigns.placeholders.noScheduling")}
    </TableCell>
    <TableCell align="center">
      {campaign.completedAt
        /* 🔴 `completedAt` es un INSTANTE: cuando termino la campana. Llevaba el formateador
           de PARED, que muestra los digitos guardados sin convertir — es decir, UTC. Medido
           el 2026-09-13 sobre una campana real: la lista decia "29/07 04:05" cuando la hora
           real fue "28/07 23:05". Un dia y cinco horas.
        
           La lista de campanas de GRUPO ya lo hacia bien. Dos pantallas, dos criterios.
           Ver doc 0412 §21. */
        ? formatDateTime(campaign.completedAt)
        : i18n.t("campaigns.placeholders.notCompleted")}
    </TableCell>
    {/* <TableCell align="center">
      {campaign.confirmation ? "Habilitada" : "Desabilitada"}
    </TableCell> */}
    <TableCell align="center">
      {campaign.status === "EM_ANDAMENTO" && (
        <IconButton
          onClick={() => handlePauseCampaign(campaign)}
          title="Pausar Campaña"
          size="small"
          className={classes.pauseIcon}
        >
          <PauseCircleOutlineIcon />
        </IconButton>
      )}
      {(campaign.status === "PROGRAMADA" || campaign.status === "EM_ANDAMENTO") && (
        <IconButton
          onClick={() => handleStopCampaign(campaign)}
          title="Parar Campaña Definitivamente"
          size="small"
          className={classes.stopIcon}
        >
          <StopIcon />
        </IconButton>
      )}
      {campaign.status === "CANCELADA" && campaign.nextScheduledAt && (
        <IconButton
          onClick={() => restartCampaign(campaign)}
          title="Reanudar Campaña"
          size="small"
          className={classes.playIcon}
        >
          <PlayCircleOutlineIcon />
        </IconButton>
      )}
      {campaign.status === "FINALIZADA" && (
        <IconButton
          size="small"
          onClick={() => handleCopyCampaign(campaign)}
          className={classes.actionIcon}
          title="Duplicar campaña"
        >
          <FileCopyIcon fontSize="small" />
        </IconButton>
      )}
      <IconButton
        size="small"
        onClick={() => handleEditCampaign(campaign)}
        className={classes.actionIcon}
        title={
          isCampaignEditable(campaign)
            ? "Editar Campaña"
            : campaign.status === "FINALIZADA" && !campaign.isRecurring
              ? "Ver Campaña (Solo Lectura)"
              : "No se puede editar esta campaña"
        }
        disabled={!canOpenCampaignModal(campaign)}
      >
        <EditIcon />
      </IconButton>

      <IconButton
        size="small"
        onClick={() => handleDeleteCampaign(campaign)}
        className={classes.deleteIcon}
        title="Eliminar Campaña"
      >
        <DeleteOutlineIcon />
      </IconButton>
    </TableCell>
  </TableRow>
);

export default CampaignTableRow;
